// 034 (s/c) — Curvas de Ganancia de Peso de la Mujer Gestante (fila 34 de
// 8.3): "Observation (category vital-signs, body weight, effectiveDateTime
// = cada control, con edad gestacional como componente)". Discreto puro.
// Puerto de packages/hce/src/hce/forms/Formulario034.tsx a shadcn/Tailwind
// — `onSubmitted` reemplazado por `onGuardado(pares)`.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { VITAL_SIGNS_CATEGORY } from './vitalSigns';
import { validar034 } from './validacion034';

const PESO_LOINC = '29463-7';
// Edad gestacional (semanas) — LOINC 49051-6 "Gestational age".
const EDAD_GESTACIONAL_LOINC = '49051-6';

export function Formulario034({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [peso, setPeso] = useState<number | ''>('');
  const [edadGestacional, setEdadGestacional] = useState<number | ''>('');
  const [intentado, setIntentado] = useState(false);
  const { guardando, error, guardar } = useGuardado();
  const errores = validar034(peso, edadGestacional);

  const handleSubmit = (): Promise<void> => guardar(async () => {
    setIntentado(true);
    if (peso === '' || errores.peso || errores.edadGestacional) return;
    await medplum.createResource({
      resourceType: 'Observation',
      status: 'final',
      category: [VITAL_SIGNS_CATEGORY],
      code: { coding: [{ system: 'http://loinc.org', code: PESO_LOINC, display: 'Peso corporal' }] },
      subject,
      encounter,
      effectiveDateTime: new Date().toISOString(),
      valueQuantity: { value: peso, unit: 'kg', system: 'http://unitsofmeasure.org', code: 'kg' },
      component:
        edadGestacional !== ''
          ? [
              {
                code: { coding: [{ system: 'http://loinc.org', code: EDAD_GESTACIONAL_LOINC, display: 'Edad gestacional' }] },
                valueQuantity: { value: edadGestacional, unit: 'semanas', system: 'http://unitsofmeasure.org', code: 'wk' },
              },
            ]
          : undefined,
    });

    const pares: Par[] = [{ pregunta: t('formularios.f034.peso'), respuesta: String(peso) }];
    if (edadGestacional !== '') pares.push({ pregunta: t('formularios.f034.edadGestacional'), respuesta: String(edadGestacional) });
    onGuardado(pares);
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Label htmlFor="f034-peso">{t('formularios.f034.peso')}</Label>
        <Input id="f034-peso" type="number" value={peso} onChange={(e) => setPeso(e.target.value === '' ? '' : Number(e.target.value))} required min={30} max={200} step="any" aria-invalid={intentado && Boolean(errores.peso)} aria-describedby={intentado && errores.peso ? 'f034-peso-error' : undefined} />
        {intentado && errores.peso && <p id="f034-peso-error" role="alert" className="text-sm text-red-600">{t(`formularios.f034.${errores.peso}`)}</p>}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="f034-edad-gestacional">{t('formularios.f034.edadGestacional')}</Label>
        <Input
          id="f034-edad-gestacional"
          type="number"
          value={edadGestacional}
          onChange={(e) => setEdadGestacional(e.target.value === '' ? '' : Number(e.target.value))}
          min={1}
          max={45}
          aria-invalid={intentado && Boolean(errores.edadGestacional)}
          aria-describedby={intentado && errores.edadGestacional ? 'f034-edad-gestacional-error' : undefined}
        />
        {intentado && errores.edadGestacional && <p id="f034-edad-gestacional-error" role="alert" className="text-sm text-red-600">{t(`formularios.f034.${errores.edadGestacional}`)}</p>}
      </div>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando || peso === ''}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
